/**
 * This client was automatically generated. ** Do Not Edit **
 */

import { apiObject, apiCallback } from '@rudderstack/rudder-sdk-node';
import * as Rudder from './rudder-node';

export enum UserGroup {
  // These users fall within the automation group (i.e. dd synthetics, load tests, automation tests)
  Automation = 'automation',
  // These users are Cventers. Whenever a user is identify, the email, company name, account name and initial refferer are properties that are used to determine if this is an internal user
  Internal = 'internal',
  // These users are considered actual customers. If an internal user didn't follow the rules when creating accounts they will be placed in the external group
  External = 'external',
  // This user is an annonymous user. Once they login or signup, they will be placed under one of the other groups
  Unknown = 'unknown'
}
export enum Persona {
  // These users are users that attend an event either in person or virtually
  Attendee = 'attendee',
  // These users are users that plan and coordinate all aspects of an event or webinar
  Planner = 'planner',
  // These users are users that present a topic or a talk during an event or webinar
  Speaker = 'speaker'
}

export interface AttendeeLoggedIn extends apiObject {
  /**
   * The current application version. Value should be grabbed from your app's package.json file
   */
  appVersion: string;
  /**
   * Identifier to reference the product that is sending the action to the downstream tools. Visit the Product Breakdown wiki to learn more
   */
  product: string;
  /**
   * Identifier to reference the business unit in the downstream tools. Visit the Product Breakdown wiki to learn more
   */
  businessUnit: string;
  /**
   * Unique identifier to reference the application that is sending the action to the downstream tools
   */
  component: string;
  /**
   * Identifier to reference the environment where the action was sent from
   */
  environmentName: string;
  /**
   * Identifier to reference the user that executed the action
   */
  userId?: string;
  /**
   * Identifier to reference the group a user belongs to
   */
  userGroup: UserGroup;
  /**
   * Identifier to represent the user's characteristics
   */
  persona: Persona;
  /**
   * Identifier to reference if an action was executed by a user logging in from cvii or a user loggin from within the application
   */
  isCVIIUser: boolean;
  /**
   * Identifier to reference the user that executed the action
   */
  attendeeId: string;
  /**
   * Unique id for the event in play
   */
  eventId: string;
  /**
   * Identifier to map different accounts across products
   */
  accountMappingId: string;
  /**
   * Identifier to represent whether event is hybrid, in person, or virtual
   */
  eventType?: string;
  /**
   * Identifier to represent if user's reg type is virtual or not
   */
  isVirtualRegtype?: boolean;
  /**
   * In case this action ends in an error, this property value should have the reason why it failed
   */
  error?: string;
  /**
   * In case this action needs to be tracked as an expected error, this property value should have the expected error message
   */
  expected_error?: string;
}

export type ViolationHandler = (message: Rudder.TrackMessage<Record<string, any>>, violations: any[]) => void;

/**
 * The default handler that is fired if none is supplied with setRudderTyperOptions.
 * If NODE_ENV="test", this handler will throw an error. Otherwise, it will log
 * a warning message to the console.
 */
export const defaultValidationErrorHandler: ViolationHandler = (message, violations) => {
  const msg = JSON.stringify(
    {
      type: 'Tracking Plan JSON Schema Validation Error',
      description:
        `You made an analytics call (${message.event}) using the auto generate client that doesn't match the ` +
        'Tracking Plan spec.',
      errors: violations
    },
    undefined,
    2
  );

  if (process.env.NODE_ENV === 'test') {
    throw new Error(msg);
  }
  console.warn(msg);
};

const missingAnalyticsNodeError = new Error(`You must set an analytics-node instance:

>	const RudderAnalytics = require('@rudderstack/rudder-sdk-node');
>	const { setRudderTyperOptions } = require('./analytics')
>
>	const analytics = new RudderAnalytics(WRITE_KEY, DATA_PLANE_URL/v1/batch)
>	setRudderTyperOptions({
>		analytics: analytics,
>	})

For more information on analytics-node, see: https://docs.rudderstack.com/stream-sources/rudderstack-sdk-integration-guides/rudderstack-node-sdk#installing-the-rudderstack-node-js-sdk
`);

let analytics: () => Rudder.AnalyticsNode | undefined = () => {
  throw missingAnalyticsNodeError;
};

/** Options to customize the runtime behavior of a RudderTyper client. */
export interface RudderTyperOptions {
  /**
   * Underlying analytics instance where analytics calls are forwarded on to.
   */
  analytics: Rudder.AnalyticsNode;
  /**
   * Handler fired when if an event does not match its spec. This handler
   * does not fire in production mode, because it requires inlining the full
   * JSON Schema spec for each event in your Tracking Plan.
   *
   * By default, it will throw errors if NODE_ENV = "test" so that tests will fail
   * if a message does not match the spec. Otherwise, errors will be logged to stderr.
   */
  onViolation?: ViolationHandler;
}

/**
 * Updates the run-time configuration of this RudderTyper client.
 * This function must be called with a configured analytics-node instance before firing
 * any analytics calls, or else a `missingAnalyticsNodeError` error will be thrown.
 *
 * @param {RudderTyperOptions} options - the options to upsert
 *
 * @typedef {Object} RudderTyperOptions
 * @property {Rudder.AnalyticsNode} analytics - Underlying analytics instance where analytics
 * 		calls are forwarded on to.
 * @property {Function} [onViolation] - Handler fired when if an event does not match its spec. This handler does not fire in
 * 		production mode, because it requires inlining the full JSON Schema spec for each event in your Tracking Plan. By default,
 * 		it will throw errors if NODE_ENV="test" so that tests will fail if a message does not match the spec. Otherwise, errors
 * 		will be logged to stderr.
 */
export function setRudderTyperOptions(options: RudderTyperOptions) {
  analytics = options.analytics ? () => options.analytics : analytics;
}

/**
 * Helper to attach metadata on RudderTyper to outbound requests.
 * This is used for attribution and debugging by the RudderStack team.
 */
function withRudderTyperContext<P, T extends Rudder.TrackMessage<P>>(message: T): T {
  return {
    ...message,
    context: {
      ...(message.context || {}),
      ruddertyper: {
        sdk: 'analytics-node',
        language: 'typescript',
        rudderTyperVersion: '',
        trackingPlanId: 'tp_2VRbjqSvKlZmsfeo1y8G4YKKwBm',
        trackingPlanVersion: '1'
      }
    }
  };
}

/**
 * A message payload for an analytics-node `.track()` call.
 * See: https://docs.rudderstack.com/stream-sources/rudderstack-sdk-integration-guides/rudderstack-node-sdk#track
 *
 * @typedef TrackMessage<PropertiesType>
 * @property {string | number} [userId] - The ID for this user in your database.
 * @property {string | number} [anonymousId] - An ID to associated with the user when you don’t know who they are.
 * @property {PropertiesType} [properties] - A dictionary of properties for the event.
 * @property {Date} [timestamp] - A Javascript date object representing when the track took place. If the track
 * 		just happened, leave it out and we’ll use the server’s time. If you’re importing data from the past make
 * 		sure you to send a timestamp.
 * @template PropertiesType
 */

/**
 * @typedef AttendeeLoggedIn
 * @property {string} appVersion - The current application version. Value should be grabbed from your app's package.json file
 * @property {string} product - Identifier to reference the product that is sending the action to the downstream tools. Visit the Product Breakdown wiki to learn more
 * @property {string} businessUnit - Identifier to reference the business unit in the downstream tools. Visit the Product Breakdown wiki to learn more
 * @property {string} component - Unique identifier to reference the application that is sending the action to the downstream tools
 * @property {string} environmentName - Identifier to reference the environment where the action was sent from
 * @property {string} [userId] - Identifier to reference the user that executed the action
 * @property {string} userGroup - Identifier to reference the group a user belongs to
 * @property {string} persona - Identifier to represent the user's characteristics
 * @property {boolean} isCVIIUser - Identifier to reference if an action was executed by a user logging in from cvii or a user loggin from within the application
 * @property {string} attendeeId - Identifier to reference the user that executed the action
 * @property {string} eventId - Unique id for the event in play
 * @property {string} accountMappingId - Identifier to map different accounts across products
 * @property {string} [eventType] - Identifier to represent whether event is hybrid, in person, or virtual
 * @property {boolean} [isVirtualRegtype] - Identifier to represent if user's reg type is virtual or not
 * @property {string} [error] - In case this action ends in an error, this property value should have the reason why it failed
 * @property {string} [expected_error] - In case this action needs to be tracked as an expected error, this property value should have the expected error message
 */

/**
 * this action tracks the users who login to AH in order to know the number of attendees that have logged in for a specific event
 *
 * Action Definition:
 * ------------------------------------------------------
 * status        : active
 * id            : ev_2VRbjzu0hiKslP4FMM6tY82VXC0
 * name          : attendee hub Attendee Logged-in
 * downstreamName: Attendee Logged-in
 * owner         : jtf2
 * externalName  : attendee signed in
 * phase         : engagement
 * release       : v1.0
 * version       : 1-0-0
 * type          : track
 * multiStep     :  no
 * trigger       : this action is triggered on the server as soon as the attendee lands to AH after providing the correct verification code in the login application
 * destinations  : Mixpanel,Snowflake
 * sdk           : analytics-node
 * ------------------------------------------------------
 *
 * @param {TrackMessage<AttendeeLoggedIn>} message - The analytics properties that will be sent to RudderStack.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 		call is fired.
 */
export function attendeeLoggedIn(message: Rudder.TrackMessage<AttendeeLoggedIn>, callback?: apiCallback): void {
  const version = '1-0-0';
  // This is passed as a property so via a transformation we use the downstreamName instead
  const downstreamName = 'Attendee Logged-in';
  const msg = withRudderTyperContext({
    properties: message,
    ...(message.userId && { userId: message.userId }),
    ...(!message.userId && { anonymousId: message.anonymousId }),
    event: 'attendee hub Attendee Logged-in'
  });

  const a = analytics();
  if (a) {
    if (msg.properties) {
      msg.properties.version = version;
      msg.properties.downstreamName = downstreamName;
    } else {
      msg.properties = { version, downstreamName };
    }
    a.track(msg, callback);
  } else {
    throw missingAnalyticsNodeError;
  }
}

const clientAPI = {
  /**
   * Updates the run-time configuration of this RudderTyper client.
   * This function must be called with a configured analytics-node instance before firing
   * any analytics calls, or else a `missingAnalyticsNodeError` error will be thrown.
   *
   * @param {RudderTyperOptions} options - the options to upsert
   *
   * @typedef {Object} RudderTyperOptions
   * @property {Rudder.AnalyticsNode} analytics - Underlying analytics instance where analytics
   * 		calls are forwarded on to.
   * @property {Function} [onViolation] - Handler fired when if an event does not match its spec. This handler does not fire in
   * 		production mode, because it requires inlining the full JSON Schema spec for each event in your Tracking Plan. By default,
   * 		it will throw errors if NODE_ENV="test" so that tests will fail if a message does not match the spec. Otherwise, errors
   * 		will be logged to stderr.
   */
  setRudderTyperOptions,
  /**
   * this action tracks the users who login to AH in order to know the number of attendees that have logged in for a specific event
   *
   * Action Definition:
   * ------------------------------------------------------
   * status        : active
   * id            : ev_2VRbjzu0hiKslP4FMM6tY82VXC0
   * name          : attendee hub Attendee Logged-in
   * downstreamName: Attendee Logged-in
   * owner         : jtf2
   * externalName  : attendee signed in
   * phase         : engagement
   * release       : v1.0
   * version       : 1-0-0
   * type          : track
   * multiStep     :  no
   * trigger       : this action is triggered on the server as soon as the attendee lands to AH after providing the correct verification code in the login application
   * destinations  : Mixpanel,Snowflake
   * sdk           : analytics-node
   * ------------------------------------------------------
   *
   * @param {TrackMessage<AttendeeLoggedIn>} message - The analytics properties that will be sent to RudderStack.
   * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
   * 		call is fired.
   */
  attendeeLoggedIn
};

export default new Proxy<typeof clientAPI>(clientAPI, {
  get(target, method) {
    if (typeof method === 'string' && target.hasOwnProperty(method)) {
      return target[method as keyof typeof clientAPI];
    }

    return () => {
      console.warn(`⚠️  You made an analytics call (${String(method)}) that can't be found. Either:
    a) Re-generate your ruddertyper client: \`npx rudder-typer\`
    b) Add it to your Tracking Plan: https://app.rudderstack.com/trackingPlans/tp_2VRbjqSvKlZmsfeo1y8G4YKKwBm`);
      const a = analytics();
      if (a) {
        a.track(
          withRudderTyperContext({
            event: 'Unknown Analytics Call Fired',
            properties: {
              method: [method as string]
            },
            userId: 'ruddertyper'
          })
        );
      }
    };
  }
});
